import type {
  DataBuilderSettingsGroup,
  DataBuilderSettingsSection,
  DataBuilderUpdateSettingsPostBody,
} from '../../../DataBuilder/api/data-builder-api.types';
import type { SettingsValue, SettingsValues } from './settings-value';

export type SectionMeta = {
  type: DataBuilderSettingsSection['type'];
  required: boolean;
};

/**
 * Assembles the `/update-settings` body from the editable values. For a
 * NON-REQUIRED text field an empty string is sent as `null`, never `''` — the
 * backend rejects `''` for optional fields. Select (`[]`) and boolean values pass
 * through untouched.
 */
export const buildSettingsUpdatePayload = (
  name: string,
  values: SettingsValues,
  sectionByKey: Record<string, SectionMeta>
): DataBuilderUpdateSettingsPostBody => {
  const payload: DataBuilderUpdateSettingsPostBody = { client_name: name };
  // SettingsUpdate permits `null` on every writable field, so widening the
  // accumulator to allow the empty-string → null swap below is sound.
  const writable = payload as Record<string, SettingsValue | null>;
  for (const [key, value] of Object.entries(values)) {
    const section = sectionByKey[key];
    const isEmptyOptionalText =
      section !== undefined &&
      section.type === 'text' &&
      !section.required &&
      value === '';
    writable[key] = isEmptyOptionalText ? null : value;
  }
  return payload;
};

export const defaultValueForSection = (
  section: DataBuilderSettingsSection
): SettingsValue => {
  switch (section.type) {
    case 'boolean':
      return section.value ?? false;
    case 'select': {
      const fallback: SettingsValue = section.multi ? [] : '';
      return section.value ?? fallback;
    }
    default:
      return section.value ?? '';
  }
};

export const seedValues = (
  groups: DataBuilderSettingsGroup[]
): SettingsValues => {
  const values: SettingsValues = {};
  for (const group of groups) {
    for (const section of group.sections) {
      values[section.key] = defaultValueForSection(section);
    }
  }
  return values;
};
